import React, { useCallback, useState } from "react";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Add from "@mui/icons-material/Add";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { useConventionIdFromRoute } from "./WithEventIdFromRoute";
import { ConventionLogo } from "./ConventionLogo";
import { formatConventionDateNoSubmission, formatFullDate, formatLocation } from "@seabrookstudios/pitch2table-core";
import Skeleton from "@mui/material/Skeleton";
import { useGamesForConvention, useConventionById } from "@seabrookstudios/pitch2table";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import { GameListEntry2 } from "../games/ListItem";
import { useMakeListView, useMakeListBuilder, idExtractor } from "../core/useMakeLayout";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SessionsForEvent } from "../sessions/SessionsForEvent";
import { PublishersForConvention } from "./PublishersForConvention";
import { AddGamesToConventionDialog } from "./AddGamesToConventionDialog";

const GamesListEntry = ({ item }) => {
  return <GameListEntry2 item={item} noAlert={false} />;
};

const EmptyGamesList = () => (
  <CardContent>
    <Typography variant="body2" color="text.secondary">
      From the game page, you can link a game to a convention. Use this to help coordination and preparation you need to
      do before the event.
    </Typography>
  </CardContent>
);

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").ConventionId} props.conventionId
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId|undefined} props.linkedPublisherId
 */
const GamesForConvention = ({ conventionId, linkedPublisherId }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const useWrappedQuery = () => useGamesForConvention(conventionId);

  const Action = useCallback(
    () => (
      <IconButton color="primary" onClick={() => setIsAddDialogOpen(true)} size="medium">
        <Add />
      </IconButton>
    ),
    []
  );

  const useMakeList = useMakeListBuilder(
    GamesListEntry,
    idExtractor,
    "Linked Games",
    EmptyGamesList,
    Action,
    undefined,
    undefined
  );

  const ListView = useMakeListView(useWrappedQuery, [], "convention-games", useMakeList);

  return (
    <>
      <ListView />
      <AddGamesToConventionDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        conventionId={conventionId}
        linkedPublisherId={linkedPublisherId}
      />
    </>
  );
};

export const EventFromRoute = () => {
  const conventionId = useConventionIdFromRoute();
  const { data: convention, isLoading } = useConventionById(conventionId);

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <List>
          <ListItem>
            <ListItemText primary={<Skeleton />} secondary={<Skeleton />} />
          </ListItem>
        </List>
      </Card>
    );
  }

  if (!convention) {
    return <EmptyDetailCard text="Event not found." />;
  }

  // Cast to CalendarEvent for formatting functions
  const event = /** @type {import("@seabrookstudios/pitch2table-core").CalendarEvent} */ (convention);

  return (
    <>
      <Card elevation={1} square={false}>
        <Stack direction="row-reverse" p={2}>
          <Stack direction="column" flex={1} justifyContent="flex-start" alignItems="center">
            <Box p={1}>
              <ConventionLogo url={convention.logoUrl} name={convention.name} />
            </Box>
          </Stack>
          <Stack direction="column" flex={1}>
            <CardHeader title={convention.name} />
            <List>
              <ListItem>
                <ListItemText primary="Where" secondary={formatLocation(event)} />
              </ListItem>
              <ListItem>
                <ListItemText primary="When" secondary={formatConventionDateNoSubmission(event)} />
              </ListItem>
              {!!convention.submissionDate && (
                <ListItem>
                  <ListItemText primary="Submissions Close" secondary={formatFullDate(convention.submissionDate)} />
                </ListItem>
              )}
            </List>
          </Stack>
        </Stack>
      </Card>
      <Box pt={1} />
      <SessionsForEvent conventionId={conventionId} convention={event} />
      <Box pt={1} />
      <GamesForConvention
        conventionId={conventionId}
        linkedPublisherId={convention.type === "PitchEvent" ? event.linkedPublisherId : undefined}
      />
      <Box pt={1} />
      <PublishersForConvention conventionId={conventionId} />
    </>
  );
};
